/* The main screen: every salon, filterable, one row each.
 *
 * What a row shows is chosen to answer "is this one all right" without opening
 * it. That is why the columns are the connections and the two clocks rather than
 * the address and the plan: a salon is fine when its book is syncing and its
 * board is being used, and everything else about it can wait until somebody has
 * a reason to look.
 *
 * The licence did not get a column of its own for that reason, and it did get a
 * filter and a place in the state reading, because it is the one thing about a
 * salon that goes wrong on a DATE rather than because something broke. "Which of
 * these is up for renewal" has no other way of being asked, and a licence that
 * has lapsed is a salon whose console has gone dark — which outranks every other
 * reading here except suspension.
 *
 * The filtering itself is in js/rules/tenant-filter.js — pure, and tested there.
 * This file is the controls and the table.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { tenantFilter } from '../rules/tenant-filter.js';

export const TenantList = {
  name: 'TenantList',
  setup: function () {
    var creating = Vue.ref(false);
    var newName = Vue.ref('');
    var newNote = Vue.ref('');

    Vue.onMounted(function () {
      if (!store.listLoaded.value) store.loadTenants();
    });

    async function create() {
      var name = newName.value.trim();
      if (!name) { api.toast('A tenant needs a name.', 'bad'); return; }
      // Through the edge function: creating a tenant is one of the four things
      // the policies cannot do — see admin/server/supabase/functions/admin-api/.
      var res = await api.call('tenants.create', { name: name, note: newNote.value || null });
      if (!res) return;
      newName.value = '';
      newNote.value = '';
      creating.value = false;
      await store.loadTenants();
      // The licence comes with the tenant — the function grants the default
      // plan as it creates one. A project with no default plan says so rather
      // than leaving somebody to find out from an empty console later.
      api.toast(res.licensed
        ? 'Created ' + name + ' on the ' + res.licensed + ' plan. It has no members yet — ' +
          'nobody can sign in to it.'
        : 'Created ' + name + ', with NO licence — no plan is marked as the default. ' +
          'Its console would offer nothing.');
      nav.goTenant(res.tenant.id);
    }

    function open(t) { nav.goTenant(t.id); }

    // The one-line health reading in the last column. Order matters: suspended
    // wins over everything, because a suspended salon's other numbers are all
    // frozen and reporting "quiet" about it would be describing the symptom.
    // A live support session outranks the rest for a different reason — it is a
    // fact about US, and it is the one thing on this screen that is costing
    // somebody something right now.
    function state(t) {
      if (store.supporting.value && store.support.tenantId === t.id) {
        return { label: 'You are in here', kind: 'warn' };
      }
      if (t.suspended_at) return { label: 'Suspended', kind: 'bad' };
      // Above "no members" and everything below it: a lapsed or missing licence
      // is a console showing a card instead of a board, which is a worse thing
      // to be true of a salon than anything further down this list. A null
      // state means this app could not read licences at all — a project without
      // part 14 of the schema — and says nothing rather than guessing.
      if (t.license_state === 'lapsed') return { label: 'Licence lapsed', kind: 'bad' };
      if (t.license_state === 'none') return { label: 'No licence', kind: 'bad' };
      if (!t.member_count) return { label: 'No members', kind: 'warn' };
      if (tenantFilter.licenseExpiring(t, Date.now())) {
        return { label: 'Renewal due', kind: 'warn' };
      }
      if (tenantFilter.isQuiet(t, Date.now())) return { label: 'Quiet', kind: 'warn' };
      return { label: 'Live', kind: 'ok' };
    }

    return {
      tenants: store.tenants,
      visible: store.visible,
      summary: store.summary,
      filter: store.filter,
      filtered: store.filtered,
      clearFilter: store.clearFilter,
      loaded: store.listLoaded,
      reload: store.loadTenants,
      ago: store.ago,
      stamp: store.stamp,
      open: open,
      state: state,
      creating: creating,
      newName: newName,
      newNote: newNote,
      create: create
    };
  },
  template: [
    '<div class="screen">',
    '  <div class="screen__head">',
    '    <div>',
    '      <h2>Tenants</h2>',
    '      <p class="screen__lead">Every salon on this project. Selecting one opens its',
    '        settings, including the ones its own console does not show.</p>',
    '    </div>',
    '    <div class="screen__actions">',
    '      <button type="button" class="btn btn--ghost btn--sm" @click="reload()">Reload</button>',
    '      <button type="button" class="btn btn--primary" @click="creating = !creating">',
    '        {{ creating ? "Cancel" : "New tenant" }}</button>',
    '    </div>',
    '  </div>',
    '',
    '  <form v-if="creating" class="card" @submit.prevent="create">',
    '    <h3>New tenant</h3>',
    '    <p class="hint">Creates the salon and nothing else: no members, no services, no',
    '      staff, no ingest key. It cannot be signed in to until somebody is attached to it',
    '      under <strong>People</strong>.</p>',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">Name</span>',
    '        <input v-model="newName" class="input" type="text" maxlength="80"',
    '          placeholder="What the business is called" />',
    '      </label>',
    '      <label class="field field--grow">',
    '        <span class="field__label">Note (optional)</span>',
    '        <input v-model="newNote" class="input" type="text" maxlength="200"',
    '          placeholder="Anything worth remembering about them" />',
    '      </label>',
    '      <button type="submit" class="btn btn--primary">Create</button>',
    '    </div>',
    '  </form>',
    '',
    '  <div class="filters">',
    '    <input v-model="filter.query" type="search" class="input filters__q"',
    '      placeholder="Name, note, mailbox host or id" />',
    '    <label class="field">',
    '      <span class="field__label">Status</span>',
    '      <select v-model="filter.status" class="input">',
    '        <option value="all">All</option>',
    '        <option value="live">Live</option>',
    '        <option value="suspended">Suspended</option>',
    '      </select>',
    '    </label>',
    '    <label class="field">',
    '      <span class="field__label">Connected</span>',
    '      <select v-model="filter.connection" class="input">',
    '        <option value="any">Anything</option>',
    '        <option value="scheduling">Booking system on</option>',
    '        <option value="no-scheduling">No booking system</option>',
    '        <option value="ingest">Has an ingest key</option>',
    '        <option value="no-ingest">No ingest key</option>',
    '      </select>',
    '    </label>',
    '    <label class="field">',
    '      <span class="field__label">Activity</span>',
    '      <select v-model="filter.activity" class="input">',
    '        <option value="any">Any</option>',
    '        <option value="active">Used recently</option>',
    '        <option value="quiet">Quiet</option>',
    '      </select>',
    '    </label>',
    '    <label class="field">',
    '      <span class="field__label">Licence</span>',
    '      <select v-model="filter.license" class="input">',
    '        <option value="any">Any</option>',
    '        <option value="licensed">In force</option>',
    '        <option value="expiring">Renewal due</option>',
    '        <option value="lapsed">Lapsed</option>',
    '        <option value="none">Never licensed</option>',
    '      </select>',
    '    </label>',
    '    <label class="field">',
    '      <span class="field__label">Sort</span>',
    '      <select v-model="filter.sort" class="input">',
    '        <option value="name">Name</option>',
    '        <option value="created">Newest</option>',
    '        <option value="activity">Last turn</option>',
    '        <option value="turns">Most turns</option>',
    '      </select>',
    '    </label>',
    '  </div>',
    '',
    '  <p class="filters__count">{{ summary }}',
    '    <button v-if="filtered" type="button" class="link-btn" @click="clearFilter">',
    '      Clear filters</button></p>',
    '',
    '  <p v-if="!loaded" class="empty">Loading tenants…</p>',
    '  <p v-else-if="!tenants.length" class="empty">No tenants on this project yet.</p>',
    '  <p v-else-if="!visible.length" class="empty">Nothing matches those filters.</p>',
    '',
    '  <table v-else class="grid">',
    '    <thead>',
    '      <tr>',
    '        <th>Salon</th>',
    '        <th>Booking system</th>',
    '        <th class="grid__num">Staff</th>',
    '        <th class="grid__num">Turns</th>',
    '        <th>Last turn</th>',
    '        <th>Book synced</th>',
    '        <th>State</th>',
    '      </tr>',
    '    </thead>',
    '    <tbody>',
    '      <tr v-for="t in visible" :key="t.id" class="grid__row" @click="open(t)"',
    '        :class="{ \'is-suspended\': t.suspended_at }">',
    '        <td>',
    '          <span class="grid__name">{{ t.name }}</span>',
    '          <span v-if="t.note" class="grid__note">{{ t.note }}</span>',
    '          <span class="grid__id" :title="t.id">{{ t.id.slice(0, 8) }}</span>',
    '        </td>',
    '        <td>',
    '          <span v-if="t.scheduling_on" class="pill pill--ok">{{ t.scheduling_plugin || "on" }}</span>',
    '          <span v-else class="pill">off</span>',
    '          <span v-if="t.scheduling_on" class="grid__sub">{{ t.mapped_count }} mapped</span>',
    '        </td>',
    '        <td class="grid__num">{{ t.staff_count }}</td>',
    '        <td class="grid__num">{{ t.turn_count }}</td>',
    '        <td :title="stamp(t.last_turn_at)">{{ ago(t.last_turn_at) }}</td>',
    '        <td :title="stamp(t.last_synced_at)">{{ ago(t.last_synced_at) }}</td>',
    '        <td><span class="pill" :class="\'pill--\' + state(t).kind">{{ state(t).label }}</span></td>',
    '      </tr>',
    '    </tbody>',
    '  </table>',
    '</div>'
  ].join('\n')
};
